import type { ViewEntity } from "@lobu/views/components";
export interface Entity extends ViewEntity { parent_id: number | null; organization_id: string; created_at: string; parent_name?: string; total_content?: number; active_connections?: number; automations_count?: number; children_count?: number }
import type { ContentItem } from "@lobu/connector-sdk";
import type { AttributeFilter } from "@lobu/core/contracts/tools/collection-selection";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
	getEntityListInitialData,
	getEntityTypeInitialData,
	getResolvedPathInitialData,
} from "../public-bootstrap";
import type { EntityPathSegment } from "../url";
import { buildOwnerRootPath } from "../url";
import {
	API_URL,
	type ApiOrgContext,
	apiCall,
	apiErrorFor,
	fetchWithTimeout,
	normalizeOrgContext,
	orgKey,
	resolveApiScope,
	resolveOrgSelector,
} from "./core";
import { createMutation, createOrgQuery } from "./hook-factory";

type OrgContextArg = ApiOrgContext | string | null | undefined;

interface AuthConfig {
	social: Record<string, boolean>;
	/**
	 * Display metadata for enabled login providers, keyed by provider.
	 * Absent on older servers — the login page falls back to the bundled icon
	 * set (then a globe + capitalized key) when it is missing.
	 */
	socialMeta?: Record<string, { name: string; faviconDomain: string | null }>;
	magicLink: boolean;
	phone: boolean;
	emailPassword: boolean;
	passkey: boolean;
	// Local-first single-user-mode (LOBU_SINGLE_USER=1). Drives copy + routing:
	// when on, the SPA shows "Set up your local install" + auto-routes to
	// /sign-up when no user exists yet, /sign-in when one does.
	singleUserMode: boolean;
	// True iff at least one (non-legacy-bootstrap) user has signed up.
	hasUser: boolean;
}

// Uses fetchWithTimeout directly (REST endpoint, not tool-based).
// Drives the login page, the anonymous sidebar, and local-install auto sign-in.
// Keyed by path because the server resolves org login providers from it.
export function useAuthConfig(options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [
			"auth-config",
			typeof window !== "undefined" ? window.location.pathname : null,
		],
		enabled: options?.enabled ?? true,
		queryFn: async () => {
			const url = new URL(`${API_URL}/api/auth-config`);
			if (typeof window !== "undefined") {
				url.searchParams.set("callbackUrl", window.location.href);
			}
			const response = await fetchWithTimeout(url.toString());
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			return (await response.json()) as AuthConfig;
		},
		staleTime: 5 * 60 * 1000,
	});
}

// ============================================================
// Public Organizations (no auth required)
// ============================================================

export interface Organization {
	id: string;
	name: string;
	slug: string;
	logo: string | null;
	description: string | null;
	created_at: string;
	is_member: boolean;
	/** Present on authenticated organization listings for the user's personal workspace. */
	is_personal?: boolean;
	visibility: "public" | "private";
}

export type OrganizationVisibility = Organization["visibility"];

export async function updateOrganizationVisibility(
	orgContext: string | ApiOrgContext,
	visibility: OrganizationVisibility,
): Promise<Organization> {
	const scope = resolveApiScope(orgContext);
	const response = await fetchWithTimeout(
		`${API_URL}/api/${scope.slug}/organization/visibility`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ visibility }),
		},
	);

	if (!response.ok) {
		throw await apiErrorFor(response);
	}

	const result = (await response.json()) as { organization?: Organization };
	if (!result.organization) {
		throw new Error(
			"Organization visibility update did not return an organization.",
		);
	}
	return result.organization;
}

// Uses fetchWithTimeout directly (REST endpoint, not tool-based)
export function useOrganizations(options?: { search?: string }) {
	return useQuery({
		queryKey: ["organizations", options?.search],
		queryFn: async () => {
			const params = new URLSearchParams();
			if (options?.search) params.append("search", options.search);

			const url = `${API_URL}/api/organizations${params.toString() ? `?${params}` : ""}`;
			const response = await fetchWithTimeout(url, { credentials: "include" });
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			const result = await response.json();
			return (result.organizations || []) as Organization[];
		},
		// Membership is authorization state and can flip after signup or an invite.
		// Never let a mounted gate reuse an old `is_member: false`.
		staleTime: 0,
		refetchOnMount: "always",
	});
}

// Entity types

export interface EntityWithChildren extends Entity {
	children?: EntityWithChildren[];
	icon?: string;
	path?: EntityPathSegment[];
}

// Default icon for unknown entity types
const DEFAULT_ENTITY_ICON = "📄";

function getEntityIcon(_entity: Entity): string {
	return DEFAULT_ENTITY_ICON;
}

// Build tree structure from flat list
function buildEntityTree(entities: Entity[]): EntityWithChildren[] {
	const entityMap = new Map<number, EntityWithChildren>();
	// Hierarchy and parent_id apply to stored rows only.
	entities = entities.filter(
		(e): e is Entity & { id: number } => typeof e.id === "number",
	);
	const roots: EntityWithChildren[] = [];

	for (const entity of entities) {
		if (typeof entity.id !== "number") continue;
		entityMap.set(entity.id, {
			...entity,
			icon: getEntityIcon(entity),
			children: [],
		});
	}

	for (const entity of entities) {
		if (typeof entity.id !== "number") continue;
		const node = entityMap.get(entity.id);
		if (!node) {
			continue;
		}
		if (entity.parent_id) {
			const parent = entityMap.get(entity.parent_id);
			if (parent) {
				parent.children = parent.children || [];
				parent.children.push(node);
				continue;
			}
		}
		roots.push(node);
	}

	const assignPaths = (
		nodes: EntityWithChildren[],
		parentPath: EntityPathSegment[] = [],
	) => {
		for (const node of nodes) {
			const path = [
				...parentPath,
				{ entity_type: node.entity_type, slug: node.slug },
			];
			node.path = path;
			if (node.children && node.children.length > 0) {
				assignPaths(node.children, path);
			}
		}
	};

	assignPaths(roots);
	return roots;
}

export const useEntities = createOrgQuery<
	[orgContext?: OrgContextArg],
	EntityWithChildren[]
>({
	queryKey: (ctx) => ["entities", orgKey(ctx)],
	tool: "manage_entity",
	body: () => ({ action: "list", limit: 500 }),
	orgContext: (orgContext) => orgContext,
	transform: (r) => buildEntityTree(r.entities || []),
	staleTime: 30000,
});

// Entity type definition
export interface EntityType {
	id: number;
	slug: string;
	name: string;
	description?: string;
	icon?: string;
	color?: string;
	/** True when slug starts with `$` (API-derived; prefer isSystemEntityType). */
	is_system?: boolean;
	metadata_schema?: Record<string, unknown>;
	event_kinds?: Record<
		string,
		{
			description?: string;
			metadataSchema?: Record<string, unknown>;
		}
	> | null;
	organization_id?: string;
	organization_slug?: string;
	entity_count?: number;
	// Derived ("view") entity types have no rows in `entities` — their data is a
	// SQL view. `backing_sql` is set iff the type is derived. `measure_columns`
	// (the view's aggregate columns) is classified on read by the server's `get`
	// — present only on a single-type fetch, not in list responses.
	backing_sql?: string | null;
	// Connection slug a connection-backed derived view reads LIVE against (the
	// external DB), instead of the org's internal tables. Set iff the derived type
	// is connection-backed; null/absent ⇒ internal. Passed to query_sql as
	// `connection` to push the view down to that connector.
	backing_source?: string | null;
	measure_columns?: string[];
	// Declared metric catalog (entity_types.metrics_config). Present in list
	// responses so the automation source-picker can derive @metric:<slug>.<measure>
	// suggestions without a second fetch. The UI consumes `measures` and the
	// `on: "entity"` segments (sidebar rows, the record list's `?segment=`);
	// the rest (eventSets/dimensions) is carried through untouched.
	metrics_config?: EntityMetricsCatalog | null;
	// Authored TYPE-level list view templates (view_template_active_tabs for
	// resource_type='entity_type'), resolved with their data_sources run LIVE by
	// the server's `get`. Present only on a single-type fetch. Absent/[] on older
	// servers, so the list switcher treats it as "no authored views".
	view_templates?: ViewTemplateTab[];
}

/** A derived entity type renders a SQL view, not stored `entities` rows. */
export function isDerivedEntityType(t: EntityType | null | undefined): boolean {
	return !!t?.backing_sql;
}

/** A governed group-by. */
interface EntityMetricDimension {
	expr: string;
	description: string;
}

/** A governed aggregation bound to a named event set. */
interface EntityMetricMeasure {
	eventSet: string;
	agg: "sum" | "count" | "min" | "max" | "count_distinct";
	description: string;
}

/**
 * The declared metric catalog for one entity type — the subset of
 * EntityMetrics (@lobu/connector-sdk) the web UI consumes. Re-declared locally
 * rather than imported because the metric runtime types aren't re-exported
 * from the connector-sdk package barrel, and the picker only needs `measures`.
 */
export interface EntityMetricsCatalog {
	measures?: Record<string, EntityMetricMeasure>;
	dimensions?: Record<string, EntityMetricDimension>;
	segments?: Record<string, EntityMetricSegment>;
}

/** A named population filter; `on: "entity"` ones can narrow the record list. */
interface EntityMetricSegment {
	description: string;
	on: "event" | "entity";
}

interface EntityListMetadata {
	page_size: number;
	has_more: boolean;
	filtered_by_type?: string;
	total_count?: number;
	limit?: number;
	offset?: number;
	sort_by?: string;
	sort_order?: "asc" | "desc";
}

export interface LinkedEntityRef {
	slug: string;
	entity_type: string;
	name: string;
}

/**
 * Resolved linked-column references, keyed by `${entityType}:${lookupField}`,
 * then by the raw lookup value found in a row's metadata. Populated by the
 * server when the entity-type schema declares `x-link-entity-type` columns.
 */
export type LinkedEntitiesMap = Record<string, Record<string, LinkedEntityRef>>;

export interface EntityListResult {
	entities: Entity[];
	linked_entities?: LinkedEntitiesMap;
	metadata: EntityListMetadata;
}

export interface ResolvedNamespace {
	slug: string;
	type: "user" | "organization";
	id: string;
	name: string | null;
}

export interface ResolvedPathEntity {
	id: number | string | null;
	entity_type: string;
	slug: string;
	name: string;
}

export interface ViewTemplateTab {
	tab_name: string;
	tab_order: number;
	json_template: Record<string, unknown>;
	version: number;
	version_id: number;
	template_data: Record<string, unknown[]> | null;
}

/**
 * Per-field human-ownership marker. A KEY PRESENT for a metadata field path
 * means a human set that field (it's human-owned); absent ⇒ automation-owned or
 * unset. May be `{}` or missing on older entities — treat missing as "no
 * fields owned".
 */
/** Per-field ownership marker: who set a field and when, plus an optional note. */
export interface FieldControl {
	note?: string | null;
	set_by?: string | null;
	set_at?: string;
}

export type EntityFieldControls = Record<string, FieldControl>;

export interface ResolvedEntityDetails extends ResolvedPathEntity {
	parent_id: number | null;
	metadata: Record<string, unknown>;
	field_controls?: EntityFieldControls;
	json_template: Record<string, unknown> | null;
	json_template_version: number | null;
	template_data: Record<string, unknown[]> | null;
	tabs: ViewTemplateTab[];
	created_at: string;
	total_content: number;
	active_connections: number;
	automations_count: number;
	// Derived ("view") entity: synthesized from the type's backing_sql, not a
	// stored row. `metadata` holds the full view row; `measure_columns` are its
	// aggregates. When set, the detail page renders read-only.
	is_derived?: boolean;
	measure_columns?: string[];
}

export interface ChildEntity {
	id: number;
	entity_type: string;
	slug: string;
	name: string;
	market: string | null;
	content_count: number;
}

export interface SiblingEntity {
	id: number;
	entity_type: string;
	slug: string;
	name: string;
	content_count: number;
}

interface BootstrapEntityTypeSummary {
	id: number;
	slug: string;
	name: string;
	description: string | null;
	icon: string | null;
	color: string | null;
	entity_count: number;
}

interface BootstrapScopeSummary {
	total_content: number;
	active_connections: number;
	automations_count: number;
	agents_count: number;
	devices_count: number;
}

interface BootstrapContentItem {
	id: number;
	entity_ids: number[];
	platform: string;
	entity_name: string | null;
	title: string | null;
	payload_type?: "text" | "markdown" | "json_template" | "media" | "empty";
	text_content: string;
	payload_data?: Record<string, unknown>;
	payload_template?: Record<string, unknown> | null;
	source_url: string | null;
	author_name: string | null;
	created_at: string;
	occurred_at: string | null;
}

interface BootstrapFeedItem {
	id: number;
	connection_id: number;
	connector_key: string;
	display_name: string | null;
	status: string;
	entity_ids: number[];
	connector_name: string | null;
	connection_name: string | null;
	event_count: number;
	created_at: string;
	updated_at: string;
}

interface BootstrapConnectorDefinition {
	key: string;
	name: string;
	description: string | null;
	icon?: string | null;
	favicon_domain?: string | null;
}

export interface ResolvePathBootstrap {
	entity_types: BootstrapEntityTypeSummary[];
	summary: BootstrapScopeSummary;
	recent_content: BootstrapContentItem[];
	recent_feeds: BootstrapFeedItem[];
	connector_definitions: BootstrapConnectorDefinition[];
}

export interface WorkspaceCounts {
	connections: number;
	automations: number;
	agents: number;
	devices: number;
	clients: number;
}

export interface ResolvePathResult {
	workspace: ResolvedNamespace;
	segments: Array<{ entity_type: string; slug: string }>;
	path: ResolvedPathEntity[];
	entity: ResolvedEntityDetails | null;
	children: ChildEntity[];
	siblings: SiblingEntity[];
	bootstrap: ResolvePathBootstrap | null;
	counts?: WorkspaceCounts;
}

function normalizeResolvedPathInput(path: string): string {
	const trimmed = path.trim();
	if (!trimmed) return "";

	const [pathname, query = ""] = trimmed.split("?", 2);
	const normalizedPath = `/${pathname.replace(/^\/+|\/+$/g, "")}`;
	return query ? `${normalizedPath}?${query}` : normalizedPath;
}

// Uses optional org context with URL-path fallback (not suited to factory)
function resolvedPathQueryOptions(
	path: string,
	orgContext?:
		| { organizationId?: string | null; slug?: string | null }
		| string
		| null,
	options?: { includeBootstrap?: boolean },
) {
	const ctx = normalizeOrgContext(orgContext);
	const orgSelector =
		ctx.organizationId || ctx.slug ? resolveOrgSelector(ctx) : undefined;
	const normalizedPath = normalizeResolvedPathInput(path);
	// Key on slug-OR-orgId (whichever's set) but not both — slug uniquely
	// identifies the org, and orgId may arrive asynchronously after first
	// mount once `/api/organizations` resolves. Keying on both meant the
	// initial render fired one request with `organizationId=undefined` and
	// refetched moments later when orgId materialised.
	return {
		queryKey: [
			"resolve-path",
			normalizedPath,
			orgKey(ctx),
			options?.includeBootstrap ?? false,
		] as const,
		queryFn: async () => {
			const result = await apiCall<ResolvePathResult>(
				"resolve_path",
				{
					path: normalizedPath,
					include_bootstrap: options?.includeBootstrap ?? false,
				},
				orgSelector,
			);
			return result;
		},
		staleTime: 300000,
		normalizedPath,
	};
}

export function useResolvedPath(
	path: string,
	orgContext?:
		| { organizationId?: string | null; slug?: string | null }
		| string
		| null,
	options?: { includeBootstrap?: boolean },
) {
	const { normalizedPath, ...opts } = resolvedPathQueryOptions(
		path,
		orgContext,
		options,
	);
	return useQuery({
		...opts,
		enabled: !!normalizedPath,
		placeholderData: getResolvedPathInitialData(normalizedPath),
	});
}

useResolvedPath.queryOptions = (
	path: string,
	orgContext?:
		| { organizationId?: string | null; slug?: string | null }
		| string
		| null,
	options?: { includeBootstrap?: boolean },
) => {
	const { normalizedPath: _omit, ...rest } = resolvedPathQueryOptions(
		path,
		orgContext,
		options,
	);
	return rest;
};

export function useWorkspaceRoot(
	owner: string | null | undefined,
	orgContext?:
		| { organizationId?: string | null; slug?: string | null }
		| string
		| null,
) {
	return useResolvedPath(buildOwnerRootPath(owner), orgContext);
}

useWorkspaceRoot.queryOptions = (
	owner: string | null | undefined,
	orgContext?:
		| { organizationId?: string | null; slug?: string | null }
		| string
		| null,
) => useResolvedPath.queryOptions(buildOwnerRootPath(owner), orgContext);

// ============================================================
// Org-scoped query hooks
// ============================================================

export const useEntitiesByType = createOrgQuery<
	[
		entityType: string,
		orgContext?: OrgContextArg,
		options?: {
			limit?: number;
			offset?: number;
			parentId?: number | null;
			search?: string;
			sortBy?: string;
			sortOrder?: "asc" | "desc";
			/** Name of an `on: "entity"` segment declared on the type. */
			segment?: string;
			filters?: AttributeFilter[];
			enabled?: boolean;
		},
	],
	EntityListResult
>({
	queryKey: (ctx, entityType, _orgContext, options) => [
		"entities-by-type",
		entityType,
		orgKey(ctx),
		options?.limit,
		options?.offset,
		options?.parentId,
		options?.search,
		options?.sortBy,
		options?.sortOrder,
		options?.segment,
		options?.filters,
	],
	tool: "manage_entity",
	body: (entityType, _orgContext, options) => ({
		action: "list",
		entity_type: entityType,
		segment: options?.segment,
		filters: options?.filters,
		limit: options?.limit,
		offset: options?.offset,
		parent_id: options?.parentId,
		search: options?.search,
		sort_by: options?.sortBy,
		sort_order: options?.sortOrder,
	}),
	orgContext: (_entityType, orgContext) => orgContext,
	placeholderData: (ctx, entityType, _orgContext, options) =>
		// The SSR seed is the unsegmented list; a segment waits for its own page.
		ctx.slug && options?.segment === undefined && !options?.filters?.length
			? options?.parentId === undefined
				? getEntityListInitialData({
						ownerSlug: ctx.slug,
						entityTypeSlug: entityType,
						limit: options?.limit,
						offset: options?.offset,
						search: options?.search,
						sortBy: options?.sortBy,
						sortOrder: options?.sortOrder,
					})
				: undefined
			: undefined,
	enabled: (ctx, entityType, _orgContext, options) =>
		!!(ctx.organizationId || ctx.slug) &&
		!!entityType &&
		options?.enabled !== false,
	staleTime: 30000,
});

function parseEntityType(et: EntityType): EntityType {
	const eventKinds = et.event_kinds as unknown;
	const count = et.entity_count == null ? undefined : Number(et.entity_count);
	return {
		...et,
		// Schema reads skip live derived queries and return a placeholder zero.
		// Only the records collection knows that total; don't present a skipped
		// count as an empty source in the map, switcher, or command palette.
		entity_count:
			!isDerivedEntityType(et) && count !== undefined && Number.isFinite(count)
				? count
				: undefined,
		metadata_schema:
			typeof et.metadata_schema === "string"
				? JSON.parse(et.metadata_schema)
				: et.metadata_schema,
		event_kinds:
			typeof eventKinds === "string" ? JSON.parse(eventKinds) : et.event_kinds,
	};
}

export const useEntityType = createOrgQuery<
	[typeSlug: string, orgContext?: OrgContextArg],
	EntityType | null
>({
	queryKey: (ctx, typeSlug) => [
		"entity-type",
		typeSlug,
		orgKey(ctx),
	],
	tool: "manage_entity_schema",
	body: (typeSlug) => ({
		schema_type: "entity_type",
		action: "get",
		slug: typeSlug,
	}),
	orgContext: (_typeSlug, orgContext) => orgContext,
	transform: (r) => (r.entity_type ? parseEntityType(r.entity_type) : null),
	placeholderData: (ctx, typeSlug) => {
		const type = ctx.slug
			? getEntityTypeInitialData(ctx.slug, typeSlug)
			: undefined;
		return type ? parseEntityType(type) : type;
	},
	enabled: (ctx, typeSlug) => !!typeSlug && !!(ctx.organizationId || ctx.slug),
	staleTime: 60000,
});

export const useEntityTypes = createOrgQuery<
	[orgContext?: OrgContextArg],
	EntityType[]
>({
	queryKey: (ctx) => ["entity-types", orgKey(ctx)],
	tool: "manage_entity_schema",
	body: () => ({ schema_type: "entity_type", action: "list" }),
	orgContext: (orgContext) => orgContext,
	transform: (r) => (r.entity_types || []).map(parseEntityType),
	staleTime: 300000,
});

/**
 * Event types the platform itself emits (`connection.deleted`, `device.online`).
 *
 * No entity type declares these, so they are absent from `event_kinds` and a
 * trigger picker built only from entity types can never show them. Served as a
 * dedicated field rather than a synthetic `$platform` entity type, because the
 * events tab builds its content-kind dropdown from the same list and platform
 * events are not savable content kinds.
 *
 * Its own query key so `useEntityTypes` keeps returning a plain array; the
 * request is the same cheap `list` call and only the trigger editor makes it.
 */
export const usePlatformEventKinds = createOrgQuery<
	[orgContext?: OrgContextArg],
	Record<string, { description?: string }>
>({
	queryKey: (ctx) => ["platform-event-kinds", orgKey(ctx)],
	tool: "manage_entity_schema",
	body: () => ({ schema_type: "entity_type", action: "list" }),
	orgContext: (orgContext) => orgContext,
	transform: (r) => r.platform_event_kinds ?? {},
	staleTime: 300000,
});

// ============================================================
// Entity Type Admin (CRUD + Audit)
// ============================================================

export interface EntityTypeAdmin extends EntityType {
	created_by?: string | null;
	created_by_username?: string | null;
	created_at?: string;
	updated_at?: string;
}

export interface EntityTypeAuditEntry {
	id: number;
	entity_type_id: number;
	action: string;
	actor: string | null;
	before_payload: Record<string, unknown> | null;
	after_payload: Record<string, unknown> | null;
	created_at: string;
}

export const useEntityTypesAdmin = createOrgQuery<
	[orgContext?: OrgContextArg],
	EntityTypeAdmin[]
>({
	queryKey: (ctx) => ["entity-types-admin", orgKey(ctx)],
	tool: "manage_entity_schema",
	body: () => ({ schema_type: "entity_type", action: "list" }),
	orgContext: (orgContext) => orgContext,
	transform: (r) =>
		(r.entity_types || []).map((row: EntityTypeAdmin) => ({
			...parseEntityType(row),
			created_by: row.created_by ?? null,
			created_at: row.created_at,
			updated_at: row.updated_at,
		})) as EntityTypeAdmin[],
	staleTime: 300000,
});

export const useEntityTypeAudit = createOrgQuery<
	[slug: string | null, orgContext?: OrgContextArg],
	EntityTypeAuditEntry[]
>({
	queryKey: (ctx, slug) => [
		"entity-type-audit",
		slug,
		orgKey(ctx),
	],
	tool: "manage_entity_schema",
	body: (slug) => ({ schema_type: "entity_type", action: "audit", slug }),
	orgContext: (_slug, orgContext) => orgContext,
	transform: (r) => r.audit_entries || [],
	enabled: (ctx, slug) => !!slug && !!(ctx.organizationId || ctx.slug),
});

// ============================================================
// Relationship Types
// ============================================================

export interface RelationshipType {
	id: number;
	slug: string;
	name: string;
	description?: string | null;
	organization_id?: string | null;
	organization_slug?: string | null;
	created_by?: string | null;
	created_by_username?: string | null;
	metadata_schema?: Record<string, unknown> | null;
	is_symmetric: boolean;
	inverse_type_id?: number | null;
	inverse_type_slug?: string | null;
	status: string;
	created_at: string;
	updated_at: string;
	deleted_at?: string | null;
	relationship_count?: number;
	/**
	 * `"authorization"` marks a type whose edges gate access (e.g. `member_of`).
	 * The server rejects every write action on one with a 403, so the UI must
	 * not offer delete/add-rule affordances for it. Optional because a server
	 * older than the read-back change omits the field entirely.
	 */
	purpose?: string | null;
}

/**
 * Mirrors the server's `PURPOSE_AUTHORIZATION` (relationship-validation.ts).
 * Types carrying it are maintained solely by the connector ACL syncs: both the
 * schema actions and the edge writes are refused, so no caller-facing surface
 * should offer to create, modify, or delete them.
 */
export const PURPOSE_AUTHORIZATION = "authorization";

export interface RelationshipTypeRule {
	id: number;
	relationship_type_id: number;
	source_entity_type_slug: string;
	target_entity_type_slug: string;
	created_at: string;
}

export const useRelationshipTypes = createOrgQuery<
	[orgContext?: OrgContextArg],
	RelationshipType[]
>({
	queryKey: (ctx) => ["relationship-types", orgKey(ctx)],
	tool: "manage_entity_schema",
	body: () => ({ schema_type: "relationship_type", action: "list" }),
	orgContext: (orgContext) => orgContext,
	transform: (r) => r.relationship_types || [],
	staleTime: 30000,
});

/** Every rule the caller can see, in one read (`list_rules` with no slug). */
export const useAllRelationshipTypeRules = createOrgQuery<
	[orgContext?: OrgContextArg],
	RelationshipTypeRule[]
>({
	queryKey: (ctx) => [
		"relationship-type-rules",
		"*",
		ctx.organizationId,
		ctx.slug,
	],
	tool: "manage_entity_schema",
	body: () => ({ schema_type: "relationship_type", action: "list_rules" }),
	orgContext: (orgContext) => orgContext,
	transform: (r) => r.rules || [],
	enabled: (ctx) => !!(ctx.organizationId || ctx.slug),
	staleTime: 30000,
});

export const useRelationshipTypeRules = createOrgQuery<
	[slug: string | null, orgContext?: OrgContextArg],
	RelationshipTypeRule[]
>({
	queryKey: (ctx, slug) => [
		"relationship-type-rules",
		slug,
		orgKey(ctx),
	],
	tool: "manage_entity_schema",
	body: (slug) => ({
		schema_type: "relationship_type",
		action: "list_rules",
		slug,
	}),
	orgContext: (_slug, orgContext) => orgContext,
	transform: (r) => r.rules || [],
	enabled: (ctx, slug) => !!slug && !!(ctx.organizationId || ctx.slug),
});

// ============================================================
// Mutation hooks
// ============================================================

export const useCreateEntityType = createMutation<{
	slug: string;
	name: string;
	description?: string;
	icon?: string;
	color?: string;
	metadata_schema?: Record<string, unknown>;
	// A `{ sql }` backing makes this a derived (SQL-view) type; null/omitted = a
	// normal stored type.
	backing?: { sql: string } | null;
}>({
	tool: "manage_entity_schema",
	body: (p) => ({ schema_type: "entity_type", action: "create", ...p }),
	invalidateKeys: ["entity-types", "entity-types-admin"],
	checkError: false,
});

export const useUpdateEntityType = createMutation<{
	slug: string;
	name?: string;
	description?: string;
	icon?: string;
	color?: string;
	metadata_schema?: Record<string, unknown>;
	// `{ sql }` sets/replaces the backing view; null reverts to a stored type.
	backing?: { sql: string } | null;
}>({
	tool: "manage_entity_schema",
	body: (p) => ({ schema_type: "entity_type", action: "update", ...p }),
	invalidateKeys: ["entity-types", "entity-types-admin", "entity-type"],
	checkError: false,
});

export const useDeleteEntityType = createMutation<string>({
	tool: "manage_entity_schema",
	body: (slug) => ({ schema_type: "entity_type", action: "delete", slug }),
	invalidateKeys: ["entity-types", "entity-types-admin"],
	checkError: false,
	successMessage: "Entity type deleted",
});

export interface CreateEntityParams {
	entityType: string;
	name: string;
	slug?: string;
	parentId?: number;
	metadata?: Record<string, unknown>;
	domain?: string;
	category?: string;
	platformType?: string;
	mainMarket?: string;
	market?: string;
	link?: string;
}

interface CreateEntityResult {
	action: "create";
	// Absent when the create is policy-gated: the server queues an approval
	// and returns `approval_queued: true` instead of the entity.
	entity?: Entity;
	approval_queued?: boolean;
	warnings?: string[];
	next_steps: string[];
}

export const useCreateEntity = createMutation<
	CreateEntityParams,
	CreateEntityResult
>({
	tool: "manage_entity",
	body: (p) => ({
		action: "create",
		entity_type: p.entityType,
		name: p.name,
		slug: p.slug,
		parent_id: p.parentId,
		metadata: p.metadata,
		domain: p.domain,
		category: p.category,
		platform_type: p.platformType,
		main_market: p.mainMarket,
		market: p.market,
		link: p.link,
	}),
	invalidateKeys: ["entities", "entities-by-type", "entity-types"],
	checkError: false,
	// A policy-gated create queues an approval instead -- the form shows its own
	// "queued for approval" notice, so suppress the misleading success toast.
	successMessage: (result) =>
		result.approval_queued ? null : "Entity created",
});

interface UpdateEntityParams {
	entityId: number;
	name?: string;
	slug?: string;
	metadata?: Record<string, unknown>;
	// Optional note explaining a human correction. Editing as the logged-in user
	// marks the edited fields human-owned server-side; `field_note` is stored on
	// that per-field ownership marker.
	field_note?: string;
}

/**
 * What `manage_entity` update answers: the record as stored after the write,
 * which fields it applied, and which it held for approval instead. A held
 * field keeps its stored value in `entity.metadata`.
 */
export interface UpdateEntityResult {
	action: "update";
	entity: {
		id: number;
		name: string;
		slug: string;
		metadata: Record<string, unknown>;
	};
	applied_fields?: string[];
	blocked_fields?: string[];
	approval_queued?: boolean;
	approval_url?: string;
}

export const useUpdateEntity = createMutation<
	UpdateEntityParams,
	UpdateEntityResult
>({
	tool: "manage_entity",
	body: (p) => ({
		action: "update",
		entity_id: p.entityId,
		name: p.name,
		slug: p.slug,
		metadata: p.metadata,
		field_note: p.field_note,
	}),
	invalidateKeys: ["entities", "entities-by-type", "resolve-path"],
	checkError: false,
});

export const useDeleteEntity = createMutation<{
	entityId: number;
	force?: boolean;
}>({
	tool: "manage_entity",
	body: (p) => ({ action: "delete", entity_id: p.entityId, force: p.force }),
	invalidateKeys: ["entities", "entities-by-type", "entity-types"],
	checkError: false,
	successMessage: "Entity deleted",
});

export const useCreateRelationshipType = createMutation<
	{
		slug: string;
		name: string;
		description?: string;
		is_symmetric?: boolean;
		inverse_type_slug?: string;
		metadata_schema?: Record<string, unknown>;
		status?: "active" | "archived";
	},
	{ relationship_type: RelationshipType }
>({
	tool: "manage_entity_schema",
	body: (p) => ({ schema_type: "relationship_type", action: "create", ...p }),
	invalidateKeys: ["relationship-types"],
	checkError: false,
	successMessage: "Relationship type created",
});

export const useAddRelationshipTypeRule = createMutation<{
	slug: string;
	source_entity_type_slug: string;
	target_entity_type_slug: string;
}>({
	tool: "manage_entity_schema",
	body: (p) => ({ schema_type: "relationship_type", action: "add_rule", ...p }),
	invalidateKeys: ["relationship-type-rules", "relationship-types"],
	checkError: false,
});

export const useRemoveRelationshipTypeRule = createMutation<number>({
	tool: "manage_entity_schema",
	body: (ruleId) => ({
		schema_type: "relationship_type",
		action: "remove_rule",
		rule_id: ruleId,
	}),
	invalidateKeys: ["relationship-type-rules", "relationship-types"],
	checkError: false,
	successMessage: "Rule removed",
});

// ============================================================
// One record by id
// ============================================================

/** A record as `manage_entity get` returns it. */
interface EntityRecord {
	id: number;
	name: string;
	slug: string;
	entity_type: string;
	parent_id: number | null;
	metadata: Record<string, unknown> | null;
}

/**
 * One record by id. Keyed under `entities` so a save invalidates it; React
 * Query dedupes concurrent reads of the same id, so many references to one
 * record cost one request. A deleted or unreadable record is a query error.
 */
export const useEntityById = createOrgQuery<
	[entityId?: number, orgContext?: OrgContextArg],
	EntityRecord
>({
	queryKey: (ctx, entityId) => [
		"entities",
		"by-id",
		entityId,
		ctx.organizationId,
		ctx.slug,
	],
	tool: "manage_entity",
	body: (entityId) => ({ action: "get", entity_id: entityId }),
	orgContext: (_entityId, orgContext) => orgContext,
	transform: (result) => result.entity as EntityRecord,
	enabled: (ctx, entityId) => !!entityId && !!(ctx.organizationId || ctx.slug),
	staleTime: 30000,
});

/**
 * The full path segments of a record, resolving its ancestors one read at a
 * time. Run on activation (opening a record), never to render a list.
 */
export async function fetchEntityPathSegments(
	entityId: number,
	orgContext: { organizationId?: string | null; slug?: string | null },
): Promise<EntityPathSegment[]> {
	const segments: EntityPathSegment[] = [];
	const visited = new Set<number>();
	let id: number | null = entityId;
	while (id !== null) {
		if (visited.has(id)) throw new Error("Cyclic entity ancestry");
		visited.add(id);
		const { entity }: { entity: EntityRecord } = await apiCall(
			"manage_entity",
			{ action: "get", entity_id: id },
			resolveOrgSelector(orgContext),
		);
		segments.unshift({ entity_type: entity.entity_type, slug: entity.slug });
		id = entity.parent_id;
	}
	return segments;
}

// ============================================================
// Entity relationships (instance-level links)
// ============================================================

export interface EntityRelationshipRow {
	id: number;
	relationship_type_slug: string;
	relationship_type_name: string;
	is_symmetric: boolean;
	from_entity_id: number | string;
	to_entity_id: number | string;
	from_entity_name: string | null;
	from_entity_slug?: string;
	from_entity_type: string | null;
	/** Immutable type ID; distinguishes types that share a slug across workspaces. */
	from_entity_type_id?: number;
	/** Server-provided URL only when the endpoint belongs to another workspace. */
	from_entity_url?: string;
	to_entity_name: string | null;
	to_entity_slug?: string;
	to_entity_type: string | null;
	to_entity_type_id?: number;
	to_entity_url?: string;
	confidence: number;
	source: string;
	created_at: string;
}

interface RelationshipReadFailure {
	relationship_id: number;
	code: string;
}

interface EntityRelationshipsResult {
	failures?: RelationshipReadFailure[];
	rows: EntityRelationshipRow[];
	countsByType: Array<{
		relationship_type_slug: string;
		relationship_type_name: string;
		count: number;
	}>;
	total?: number;
	countsScope?: "page";
	hasMore: boolean;
}

interface EntityRelationshipsPage {
	/** Only links of this relationship type (its slug). */
	relationshipType?: string;
	limit?: number;
	offset?: number;
}

/**
 * An entity's current relationships (the `entity_relationships` rows), both
 * directions: one page of readable rows and counts for that page. This is the DATA-level view — distinct from the schema ERD,
 * which draws declared type rules. The caller projects the peer from the
 * requested entity's id; the transform keeps raw rows so direction stays
 * available.
 */
export const useEntityRelationships = createOrgQuery<
	[
		entityId?: number,
		orgContext?: OrgContextArg,
		page?: EntityRelationshipsPage,
	],
	EntityRelationshipsResult
>({
	queryKey: (ctx, entityId, _orgContext, page) => [
		"entity-relationships",
		ctx.organizationId,
		ctx.slug,
		entityId,
		page?.relationshipType ?? null,
		page?.limit ?? null,
		page?.offset ?? 0,
	],
	tool: "manage_entity",
	body: (entityId, _orgContext, page) => ({
		action: "list_links",
		entity_id: entityId,
		direction: "both",
		relationship_type_slug: page?.relationshipType,
		limit: page?.limit ?? 50,
		offset: page?.offset ?? 0,
	}),
	orgContext: (_entityId, orgContext) => orgContext,
	transform: (result) => ({
		rows: (result.relationships ?? []) as EntityRelationshipRow[],
		failures: (result.relationship_failures ?? []) as RelationshipReadFailure[],
		countsByType: (result.counts_by_type ??
			[]) as EntityRelationshipsResult["countsByType"],
		total:
			result.metadata?.total === undefined
				? undefined
				: Number(result.metadata.total),
		countsScope: result.metadata?.counts_scope,
		hasMore: result.metadata?.has_more === true,
	}),
	enabled: (ctx, entityId) => !!entityId && !!(ctx.organizationId || ctx.slug),
	staleTime: 30000,
});

/** A record of a source-backed type: no stored row, addressed by type + key. */
export interface SourceRecordRef {
	type: string;
	key: string;
}

/** A feed that failed during a live record read; its rows are missing, not absent. */
export interface SourceReadFailure {
	feed_id: number;
	error: string;
}

interface SourceRecordEvent
	extends Pick<
		ContentItem,
		| "semantic_type"
		| "payload_type"
		| "payload_data"
		| "payload_template"
		| "attachments"
	> {
	feed_id: number;
	platform: string;
	origin_id: string;
	origin_type: string | null;
	title: string | null;
	author_name: string | null;
	metadata: Record<string, unknown>;
	payload_text: string | null;
	source_url: string | null;
	occurred_at: string;
}

/** One cursor chain: refreshing a page recomputes the following cursors. */
export function useSourceRecordActivity(
	record: SourceRecordRef,
	orgContext?: OrgContextArg,
	filters?: {
		platforms?: string[];
		connectionIds?: number[] | null;
		feedIds?: number[] | null;
	},
) {
	const ctx = normalizeOrgContext(orgContext);
	return useInfiniteQuery({
		queryKey: [
			"source-record-activity",
			ctx.organizationId,
			ctx.slug,
			record.type,
			record.key,
			filters?.platforms,
			filters?.connectionIds,
			filters?.feedIds,
		],
		initialPageParam: undefined as string | undefined,
		queryFn: async ({ pageParam }) => {
			const result = await apiCall<{
				content: SourceRecordEvent[];
				record_cursor?: string;
				record_failures?: SourceReadFailure[];
			}>(
				"read_knowledge",
				{
					record,
					platforms: filters?.platforms,
					connection_ids: filters?.connectionIds ?? undefined,
					feed_ids: filters?.feedIds ?? undefined,
					limit: 25,
					...(pageParam ? { record_cursor: pageParam } : {}),
				},
				resolveApiScope(ctx),
			);
			return {
				events: result.content,
				nextCursor: result.record_cursor,
				failures: result.record_failures ?? [],
			};
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
		enabled: !!(ctx.organizationId || ctx.slug),
		staleTime: 30000,
	});
}

export interface SourceRecordLink {
	relationship_type: string;
	direction: "outgoing" | "incoming";
	entity_type: string;
	key: string;
	name: string;
	occurred_at: string;
	source_url: string | null;
}

/** A source-backed record's relationships, read live from its source. */
export function useSourceRecordLinks(
	record: SourceRecordRef,
	orgContext?: OrgContextArg,
) {
	const ctx = normalizeOrgContext(orgContext);
	const query = useInfiniteQuery({
		queryKey: [
			"source-record-links",
			ctx.organizationId,
			ctx.slug,
			record.type,
			record.key,
		],
		initialPageParam: null as string | null,
		queryFn: async ({ pageParam }) => {
			const result = await apiCall<{
				relationships?: EntityRelationshipRow[];
				relationship_failures?: RelationshipReadFailure[];
				record_links: SourceRecordLink[];
				record_failures: SourceReadFailure[];
				next_cursor: string | null;
			}>(
				"manage_entity",
				{ action: "list_links", record, limit: 100, cursor: pageParam },
				resolveApiScope(ctx),
			);
			return {
				links: result.record_links,
				relationships: result.relationships ?? [],
				relationshipFailures: result.relationship_failures ?? [],
				failures: result.record_failures,
				nextCursor: result.next_cursor,
			};
		},
		getNextPageParam: (page) => page.nextCursor,
		enabled: !!(ctx.organizationId || ctx.slug),
		staleTime: 30000,
		// Retry failed source reads only on explicit user action.
		retry: false,
	});
	// Different source observations can describe the same relationship. Merge
	// by destination and relationship semantics, never by the event's version id.
	const links = new Map<string, SourceRecordLink>();
	const relationships = new Map<number, EntityRelationshipRow>();
	const relationshipFailures = new Map<number, RelationshipReadFailure>();
	for (const page of query.data?.pages ?? []) {
		for (const failure of page.relationshipFailures ?? []) {
			relationshipFailures.set(failure.relationship_id, failure);
			relationships.delete(failure.relationship_id);
		}
		for (const row of page.relationships) {
			relationships.set(row.id, row);
			relationshipFailures.delete(row.id);
		}
		for (const link of page.links) {
			const key = JSON.stringify([
				link.relationship_type,
				link.direction,
				link.entity_type,
				link.key,
			]);
			const previous = links.get(key);
			if (!previous || previous.occurred_at < link.occurred_at)
				links.set(key, link);
		}
	}
	return {
		...query,
		data: query.data
			? {
					relationships: [...relationships.values()],
					relationshipFailures: [...relationshipFailures.values()],
					links: [...links.values()].sort((a, b) =>
						b.occurred_at.localeCompare(a.occurred_at),
					),
					// Each continuation retries previously failed streams, so the latest
					// page reports the current failures, not already-recovered ones.
					failures: query.data.pages.at(-1)?.failures ?? [],
				}
			: undefined,
	};
}
